// Sidebar state hook (T-037, plan §11 step 7, spec §4.5 + §4.6).
//
// Combines two concerns:
//   1. **Desktop collapse**: a user-controlled toggle (persisted in
//      localStorage as `sidebar.collapsed`) that switches the sidebar
//      between w-64 (256px) and w-20 (80px). When collapsed, labels
//      hide and icons stay centered.
//   2. **Mobile drawer**: at viewport widths <1024px, the sidebar is
//      replaced by a hamburger button + slide-over drawer. The drawer
//      open/close state is local (not persisted — every page load
//      starts closed).
//
// Exposes a single `mode`:
//   - 'desktop-expanded'   — viewport ≥1024px, collapsed=false
//   - 'desktop-collapsed'  — viewport ≥1024px, collapsed=true
//   - 'mobile'             — viewport <1024px (drawer-driven)

import { useCallback, useEffect, useState } from 'react';

const COLLAPSED_KEY = 'sidebar.collapsed';
const DESKTOP_BREAKPOINT_MIN_PX = 1024; // matches Tailwind `lg:` breakpoint

export type SidebarMode = 'desktop-expanded' | 'desktop-collapsed' | 'mobile';

export interface SidebarState {
  mode: SidebarMode;
  mobileDrawerOpen: boolean;
  toggleCollapsed: () => void;
  openMobileDrawer: () => void;
  closeMobileDrawer: () => void;
}

function readPersistedCollapsed(): boolean {
  // Defensive — localStorage can throw in Safari Private Browsing,
  // and `window` is undefined under SSR (we don't ship SSR but keep
  // the guard so test environments without a DOM don't blow up).
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function writePersistedCollapsed(value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(COLLAPSED_KEY, value ? '1' : '0');
  } catch {
    // Private browsing / disabled storage — no-op. The toggle still
    // works for the current session.
  }
}

function readIsDesktop(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return true;
  return window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT_MIN_PX}px)`).matches;
}

export function useSidebarState(): SidebarState {
  const [collapsed, setCollapsed] = useState<boolean>(readPersistedCollapsed);
  const [isDesktop, setIsDesktop] = useState<boolean>(readIsDesktop);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Listen for viewport width crossings — addEventListener vs
  // addListener for cross-browser compatibility.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(
      `(min-width: ${DESKTOP_BREAKPOINT_MIN_PX}px)`,
    );
    function onChange(e: MediaQueryListEvent | MediaQueryList) {
      setIsDesktop(e.matches);
    }
    // Initial sync (in case a hot reload mangled state).
    setIsDesktop(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  // When viewport crosses from mobile → desktop, auto-close the drawer
  // so a stale `mobileDrawerOpen=true` doesn't render an unwanted
  // backdrop on desktop.
  useEffect(() => {
    if (isDesktop && mobileDrawerOpen) setMobileDrawerOpen(false);
  }, [isDesktop, mobileDrawerOpen]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((v) => {
      const next = !v;
      writePersistedCollapsed(next);
      return next;
    });
  }, []);

  const openMobileDrawer = useCallback(() => setMobileDrawerOpen(true), []);
  const closeMobileDrawer = useCallback(() => setMobileDrawerOpen(false), []);

  const mode: SidebarMode = !isDesktop
    ? 'mobile'
    : collapsed
      ? 'desktop-collapsed'
      : 'desktop-expanded';

  return {
    mode,
    mobileDrawerOpen,
    toggleCollapsed,
    openMobileDrawer,
    closeMobileDrawer,
  };
}
